// PIM-Angebotsliste (Templates/Offer/List.html, Partials/Offer/Filter.html): filtert bei
// jeder Änderung ohne Absenden. Lädt die Seite mit den Formularwerten per fetch und
// übernimmt nur die [data-offer-swap]-Bereiche (Inhalt + Klassen), damit offene
// Dropdowns, Checkbox-Zustand und Fokus erhalten bleiben. Der Server bleibt die
// einzige Quelle für Trefferliste, Zähler und Chips.
const PLACE_DEBOUNCE_MS = 500;
const PLACE_MIN_LENGTH = 3;

export function initOfferFilter() {
  const form = document.querySelector('form[data-offer-form]');
  if (!form) return;

  const panel = form.querySelector('#offer-filter-panel');
  const trigger = form.querySelector('[data-offer-filter-trigger]');
  const results = form.querySelector('[data-offer-results]');
  const placeInput = form.querySelector('[data-offer-place]');
  const radiusInput = form.querySelector('[data-offer-radius]');
  const radiusOutput = form.querySelector('[data-offer-radius-output]');
  const dropdowns = form.querySelectorAll('[data-filter-group-details]');

  let controller = null;
  let placeTimer = null;
  let lastUrl = null;

  const buildUrl = () => {
    const url = new URL(form.getAttribute('action') || window.location.href, window.location.href);
    // Ein natives GET-Formular ersetzt den Query-String der action; hier bleiben die
    // Parameter der action (z. B. Standortseite) erhalten und die Formularwerte kommen dazu.
    // Leere Werte und die Extbase-Felder __referrer/__trustedProperties (für das Array-
    // Argument "filter" nicht nötig) entfallen, damit die URL teilbar kurz bleibt.
    new FormData(form).forEach((value, key) => {
      if (value === '' || key.includes('[__')) return;
      url.searchParams.append(key, value);
    });
    return url.toString();
  };

  const swap = (doc) => {
    form.querySelectorAll('[data-offer-swap]').forEach((target) => {
      const source = doc.querySelector(`form[data-offer-form] [data-offer-swap="${target.dataset.offerSwap}"]`);
      if (!source) return;
      target.innerHTML = source.innerHTML;
      target.className = source.className;
    });
  };

  const load = async (url, { focusItem = -1 } = {}) => {
    if (url === lastUrl) return;
    lastUrl = url;

    if (controller) controller.abort();
    controller = new AbortController();
    form.setAttribute('aria-busy', 'true');
    results?.classList.add('opacity-50');

    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      swap(doc);
      window.history.replaceState(null, '', url);
      // "Weitere Kurse laden": Fokus auf den ersten nachgeladenen Kurs (Tastatur/Screenreader)
      if (focusItem >= 0) {
        form.querySelectorAll('[data-offer-item]')[focusItem]?.querySelector('a')?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fallback: normale Navigation, damit der Besucher trotzdem ein Ergebnis sieht
      window.location.href = url;
      return;
    }

    form.removeAttribute('aria-busy');
    results?.classList.remove('opacity-50');
  };

  const refresh = () => {
    window.clearTimeout(placeTimer);
    load(buildUrl());
  };

  const resetRadius = () => {
    if (!radiusInput) return;
    radiusInput.value = radiusInput.dataset.defaultValue;
    if (radiusOutput) radiusOutput.textContent = radiusInput.value;
  };

  // Ausgangszustand, damit eine unveränderte Eingabe keinen Request auslöst
  lastUrl = buildUrl();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    refresh();
  });

  form.querySelectorAll('[data-filter-checkbox]').forEach((checkbox) => {
    checkbox.addEventListener('change', refresh);
  });

  form.querySelector('[data-offer-sort]')?.addEventListener('change', refresh);

  if (placeInput) {
    placeInput.addEventListener('input', () => {
      window.clearTimeout(placeTimer);
      const length = placeInput.value.trim().length;
      if (length === 0 || length >= PLACE_MIN_LENGTH) {
        placeTimer = window.setTimeout(refresh, PLACE_DEBOUNCE_MS);
      }
    });
    placeInput.addEventListener('change', refresh);
  }

  if (radiusInput) {
    radiusInput.addEventListener('input', () => {
      if (radiusOutput) radiusOutput.textContent = radiusInput.value;
    });
    // Ohne Ort wirkt der Umkreis nicht - dann kein Request
    radiusInput.addEventListener('change', () => {
      if (placeInput && placeInput.value.trim() !== '') refresh();
    });
  }

  form.addEventListener('click', (event) => {
    const chipRemove = event.target.closest('[data-offer-chip-remove]');
    if (chipRemove) {
      const { filterGroup, filterValue } = chipRemove.dataset;
      const hiddenFilter = form.querySelector(`[data-filter-hidden][data-filter-group="${filterGroup}"]`);
      if (filterGroup === 'place') {
        if (placeInput) placeInput.value = '';
        resetRadius();
      } else if (hiddenFilter) {
        // Filter ohne Facette (z. B. Berufsbild): verstecktes Feld leeren
        hiddenFilter.value = '';
      } else {
        const checkbox = form.querySelector(`[data-filter-checkbox][data-filter-group="${filterGroup}"][data-filter-value="${filterValue}"]`);
        if (checkbox) checkbox.checked = false;
      }
      refresh();
      return;
    }

    if (event.target.closest('[data-offer-filter-reset]')) {
      event.preventDefault();
      form.querySelectorAll('[data-filter-checkbox]').forEach((checkbox) => { checkbox.checked = false; });
      form.querySelectorAll('[data-filter-hidden]').forEach((input) => { input.value = ''; });
      if (placeInput) placeInput.value = '';
      resetRadius();
      dropdowns.forEach((details) => details.removeAttribute('open'));
      refresh();
      return;
    }

    const moreLink = event.target.closest('[data-offer-more]');
    if (moreLink) {
      event.preventDefault();
      load(moreLink.href, { focusItem: form.querySelectorAll('[data-offer-item]').length });
    }
  });

  // Mobile-Overlay öffnen/schließen (Prinzip wie Kurs-Filter in main.js)
  if (panel && trigger) {
    const openPanel = () => {
      panel.classList.remove('hidden');
      trigger.setAttribute('aria-expanded', 'true');
    };
    const closePanel = () => {
      panel.classList.add('hidden');
      trigger.setAttribute('aria-expanded', 'false');
    };

    trigger.addEventListener('click', openPanel);
    form.querySelectorAll('[data-offer-filter-close]').forEach((button) => button.addEventListener('click', closePanel));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.classList.contains('hidden')) {
        closePanel();
        trigger.focus();
      }
    });
  }

  // Nur ein Filter-Dropdown gleichzeitig offen; Klick außerhalb schließt
  dropdowns.forEach((details) => {
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      dropdowns.forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    });
  });

  document.addEventListener('click', (event) => {
    dropdowns.forEach((details) => {
      if (details.open && !details.contains(event.target)) {
        details.removeAttribute('open');
      }
    });
  });
}
